import { useEffect, useState } from '@integrations/react'
import { useLocation } from '@integrations/routing'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { isValidArray } from 'mmfintech-commons'

/** Router state the Accounts page (and the send-money selection) pass so a flow opens on the account being viewed. */
export interface PreSelectedAccountState {
  preSelectedAccount?: number
}

/**
 * Selects the account named in the router state once the flow's accounts have loaded. Applied once, so a later
 * manual choice is never overwritten; when the account is not available in this flow the flow's own default stays.
 * The guard is state, not a ref: StrictMode runs mount effects twice from the same render, and a ref would skip the
 * second run while the flow's own default-account effect runs again and wins.
 */
export function usePreselectedAccount(
  accounts: AccountBalanceResponse[] | undefined,
  setSelectedAccount: (account: AccountBalanceResponse) => void
) {
  const preSelectedAccount = (useLocation().state as PreSelectedAccountState | null)?.preSelectedAccount
  const [applied, setApplied] = useState(false)

  useEffect(() => {
    if (!preSelectedAccount || applied || !isValidArray(accounts)) return
    setApplied(true)
    const account = accounts.find((item) => item.id === preSelectedAccount)
    if (account) setSelectedAccount(account)
  }, [accounts, applied, preSelectedAccount, setSelectedAccount])
}
